/**
 * CORE-AC — the OptiFinish presentation, and the disclosure that belongs to it.
 *
 * CORE-AC's remaining constitutional item is disclosure on the **finish time**:
 * a finish time inheriting a technician's efficiency must say when that
 * efficiency was asserted rather than learned.
 *
 * The first version of that disclosure rendered under *Tech OptiTime*, several
 * conditional timing rows away from the number it described. This component
 * exists so the two cannot drift apart again: the finish time and its basis are
 * **one unit**, rendered together, from the same technician record the time was
 * derived from.
 *
 * It is deliberately small. `TicketCard` keeps the surrounding conditional rows
 * — the rollover gate, the EOD risk banner — because those answer different
 * questions; this owns the finish time and what it rests on, and nothing else.
 */

import { cn } from '@/lib/utils';
import { EfficiencyBasisBadge } from '@/components/tickets/EfficiencyBasisBadge';
import { TicketProvenanceBadge } from '@/components/tickets/TicketProvenanceBadge';
import type { EfficiencyBasisInput } from '@/utils/efficiencyDisclosure';

interface OptiFinishRowProps {
  /** The finish time exactly as it should read. Already overridden if needed. */
  displayValue: string;
  /** Rollover gating only changes the emphasis, never the disclosure. */
  requiresRolloverGate?: boolean;
  /**
   * The technician whose efficiency produced the time above.
   *
   * `undefined` when the ticket has no technician: there is no inherited
   * efficiency to disclose, so no basis is shown.
   */
  technician?: EfficiencyBasisInput;
}

export function OptiFinishRow({
  displayValue,
  requiresRolloverGate,
  technician,
}: OptiFinishRowProps) {
  return (
    <>
      <div
        className="flex items-center justify-between text-xs"
        data-testid="ticket-optifinish"
      >
        <span className="flex items-center gap-1 text-muted-foreground">
          OptiFinish:
          {/* Core-100 R4 (CORE-AD/AE GAP-001): the finish is DERIVED, not measured */}
          <TicketProvenanceBadge factGroupId="ticket.optiFinish" />
        </span>
        <span
          className={cn(
            'font-medium',
            requiresRolloverGate && 'text-orange-700 dark:text-orange-300',
          )}
        >
          {displayValue}
        </span>
      </div>
      {technician && (
        <EfficiencyBasisBadge
          efficiencyPercentage={technician.efficiencyPercentage}
          efficiencySource={technician.efficiencySource}
          efficiencySampleSize={technician.efficiencySampleSize}
        />
      )}
    </>
  );
}
